import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the AssignmentQuotaView.
 * @extends JetView
 */
export default class AssignmentQuotaView extends JetView {
    /**
     * Configures the UI components of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat); // Date formatting function

        // Extending the summTreeColumn filter for custom aggregation
        webix.ui.datafilter.summTreeColumn = webix.extend(
            {
                /**
                 * Refreshes the summary value in the tree column.
                 * @param {Object} master - The master data table.
                 * @param {Object} node - The current node in the tree.
                 * @param {Object} value - The value object containing columnId.
                 */
                refresh: function (master, node, value) {
                    var result = 0; // Variable to hold the sum result
                    var resultFormat = 0; // Variable for formatted result
                    master.data.each(function (obj) {
                        if (obj.$level == 1) result += obj[value.columnId]; // Summing values at level 1
                    });
                    // Formatting the result based on the column type
                    if (value.columnId === 'CantidadTickets') {
                        resultFormat = webix.i18n.intFormat(result);
                    } else {
                        resultFormat = webix.i18n.priceFormat(result);
                    }
                    node.firstChild.innerHTML = resultFormat; // Updating the node with formatted result
                },
            },
            webix.ui.datafilter.summColumn, // Extending from the existing summColumn filter
        );

        return {
            rows: [
                {
                    view: 'datatable', // DataTable component
                    id: 'datatable', // Unique ID for the DataTable
                    css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
                    select: true, // Enable row selection
                    columns: [
                        { id: 'Id', header: _('Id'), width: 100, sort: 'int' }, // Column for ID
                        { id: 'Date', header: _('Fecha'), width: 150 }, // Column for Date
                        {
                            id: 'UserId',
                            header: _('ID Usuario'),
                            width: 100,
                            sort: 'int', // Column for User ID with sorting
                        },
                        {
                            id: 'UserName',
                            header: _('Nombre del usuario'),
                            width: 150,
                            hidden: !devConfig.getPermAction(
                                'sensitiveInformationNames',
                                'Add', // Conditional visibility based on permissions
                            ),
                        },
                        {
                            id: 'TypeQuota',
                            header: _('Tipo Cupo'),
                            editor: 'combo', // Combo editor for selection
                            options: [
                                { id: 'A', value: _('Apuestas') }, // Option for Bets
                                { id: 'R', value: _('Recargas') }, // Option for Reloads
                            ],
                            width: 100,
                        },
                        {
                            id: 'TypeTransaction',
                            header: _('Tipo Transacción'),
                            editor: 'combo', // Combo editor for transaction type
                            options: [
                                { id: 'E', value: _('Deposito') }, // Option for Deposit
                                { id: 'S', value: _('Salida') }, // Option for Withdrawal
                            ],
                            width: 150,
                        },
                        {
                            id: 'Amount',
                            header: _('Valor'),
                            format: webix.i18n.priceFormat, // Formatting for price
                            exportType: 'number', // Export type for the column
                            width: 100,
                        },
                        { id: 'Currency', header: _('Moneda'), width: 100 }, // Column for Currency
                        {
                            id: 'NameBank',
                            header: _('Información de banco'),
                            width: 200, // Column for Bank Information
                        },
                        {
                            id: 'transactionId',
                            header: _('Id transacción'),
                            width: 200, // Column for Transaction ID
                        },
                    ],
                    tooltip: true, // Enable tooltips
                    math: true, // Enable mathematical operations
                    loadahead: 0, // Load ahead configuration
                    datafetch: devConfig.getDefaultDataFetch(), // Default data fetch configuration
                    pager: 'pagerA', // Pager ID for pagination
                    on: {
                        /**
                         * Event handler for before loading data.
                         */
                        onBeforeLoad: function () {
                            this.showOverlay(
                                "<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                            ); // Show loading overlay
                        },
                        /**
                         * Event handler for after loading data.
                         */
                        onAfterLoad: function () {
                            this.hideOverlay(); // Hide loading overlay
                        },
                    },
                    /**
                     * Event handler for when the DataTable is ready.
                     */
                    ready: function () {
                        if (!this.count()) {
                            webix.extend(this, webix.OverlayBox); // Extend with OverlayBox functionality
                            this.showOverlay(
                                "<div class='no-data' style='margin:75px; font-size:18px;'>" +
                                    _('No hay datos') + // Show no data message
                                    '</div>',
                            );
                        }
                    },
                },
                {
                    view: 'treetable', // TreeTable component
                    id: 'treetable', // Unique ID for the TreeTable
                    tooltip: true, // Enable tooltips
                    scrollY: true, // Enable vertical scrolling
                    footer: true, // Show footer
                    scheme: {
                        $group: {
                            by: 'Assigned', // Grouping by Assigned field
                            map: {
                                Amount: ['Amount', 'sum'], // Summing Amount for grouped rows
                                Currency: ['Currency'], // Mapping Currency
                            },
                        },
                    },
                    columns: [
                        {
                            id: 'Assigned',
                            header: _('Asignado Por'), // Column for Assigned By
                            width: 250,
                            template: function (obj, common) {
                                if (obj.$group)
                                    return (
                                        common.treetable(obj, common) + obj.value // Display group value
                                    );
                                return '';
                            },
                            footer: _('Total:'), // Footer label for total
                        },
                        { id: 'Date', header: _('Fecha'), width: 150 }, // Column for Date
                        {
                            id: 'TypeQuota',
                            header: _('Tipo Cupo'),
                            editor: 'combo', // Combo editor for quota type
                            options: [
                                { id: 'A', value: _('Apuestas') }, // Option for Bets
                                { id: 'R', value: _('Recargas') }, // Option for Reloads
                            ],
                            width: 100,
                        },
                        {
                            id: 'TypeTransaction',
                            header: _('Tipo Transacción'),
                            editor: 'combo', // Combo editor for transaction type
                            options: [
                                { id: 'E', value: _('Deposito') }, // Option for Deposit
                                { id: 'S', value: _('Salida') }, // Option for Withdrawal
                            ],
                            width: 150,
                        },
                        {
                            id: 'Amount',
                            header: _('Valor'),
                            format: webix.i18n.priceFormat, // Formatting for price
                            footer: { content: 'summTreeColumn' }, // Footer content for summation
                            exportType: 'number', // Export type for the column
                            width: 100,
                        },
                        { id: 'Currency', header: _('Moneda'), width: 100 }, // Column for Currency
                    ],
                },
            ],
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The main grid component.
     */
    init(grid) {
        var url = devConfig.geturlApi() + 'es/Agent/AssignmentQuota'; // API endpoint for fetching data
        var filter = ''; // Filter string for API requests
        var grid = $$('datatable'); // Reference to the DataTable
        var grid2 = $$('treetable'); // Reference to the TreeTable
        grid2.hide(); // Initially hide the TreeTable
        $$('toolbar2').hide(); // Initially hide the second toolbar

        // Event listener for filtering data
        this.on(this.app, 'assignmentQuota:filter', (filterData) => {
            var object = Object.entries(filterData); // Convert filter data to entries
            var string1 = ''; // Query string for API request
            var typeTable = 0; // Variable to determine table type

            // Loop through filter data to build query string
            for (var i = 0; i < object.length; i++) {
                if (object[i][0] == 'Type') {
                    if (object[i][1] == 1) {
                        typeTable = 1; // Set typeTable based on filter
                    }
                }
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    if (object[i][0] == 'Datepicker') {
                        var date = object[i][1].split(' - '); // Split date range
                        string1 = string1 + '&dateFrom=' + date[0]; // Add dateFrom to query
                        string1 = string1 + '&dateTo=' + date[1]; // Add dateTo to query
                    } else {
                        if (object[i][0] != 'Date') {
                            string1 = string1 + '&' + object[i][0] + '=' + object[i][1]; // Add other filters to query
                        } else {
                            if (object[i][0] == 'Date') {
                                var date = object[i][1].replace(' 00:00:00', ''); // Clean date string
                                string1 = string1 + '&dateFrom=' + date; // Add dateFrom to query
                                string1 = string1 + '&dateTo=' + date; // Add dateTo to query
                            }
                        }
                    }
                } else {
                    if (typeof object[i][1] === 'object') {
                        var object2 = Object.entries(object[i][1]); // Convert nested object to entries
                        for (var j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                if (object2[j][0] === 'start') {
                                    var d = new Date(object2[j][1]); // Parse start date
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Add dateFrom to query
                                } else {
                                    if (object2[j][0] === 'end') {
                                        var d = new Date(object2[j][1]); // Parse end date
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Add dateTo to query
                                    }
                                }
                            }
                        }
                    }
                }
            }
            // Load data based on the type of table selected
            if (typeTable == 0) {
                grid.show(); // Show DataTable
                grid2.hide(); // Hide TreeTable
                grid.clearAll(); // Clear existing data in DataTable
                grid.load(
                    url + '?start=0&count=' + grid.data.$pagesize + string1, // Load data with query string
                );
                $$('toolbar').show(); // Show first toolbar
                $$('toolbar2').hide(); // Hide second toolbar
            } else {
                grid2.show(); // Show TreeTable
                grid.hide(); // Hide DataTable
                grid2.clearAll(); // Clear existing data in TreeTable
                grid2.showOverlay(
                    "<div id='contenedor'; class='container-preload'><div style='position: fixed; top: calc(50% - 57px);left: calc(50% - 60px);width: 120px;' class='progress'><div class='icon-preload girando'></div><div class='load-text'>Loading... </div></div></div>",
                ); // Show loading overlay for TreeTable
                webix
                    .ajax()
                    .headers({
                        'Content-type': 'application/json', // Set content type for AJAX request
                    })
                    .get(
                        devConfig.geturlApi() + 'es/Agent/AssignmentQuota?' + string1, // Fetch data from API with query string
                        {
                            success: function (text, data, XmlHttpRequest) {
                                var resp = JSON.parse(text); // Parse response text to JSON
                                grid2.clearAll(); // Clear existing data in TreeTable
                                if (!resp.HasError && resp.Data != undefined && resp.Data.length > 0) {
                                    grid2.parse(resp.Data); // Parse and load data into TreeTable
                                    grid2.hideOverlay(); // Hide loading overlay
                                }
                            },
                        },
                    );
                $$('toolbar2').show(); // Show second toolbar
                $$('toolbar').hide(); // Hide first toolbar
            }
            $$('filterPopup').hide(); // Hide filter popup
        });
    }
}

// FILE DOCUMENTED
